import React, { useState, useEffect, useContext, useRef } from 'react';
import { Box, Typography, IconButton, CircularProgress, Button } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import WestRoundedIcon from '@mui/icons-material/WestRounded';
import EastRoundedIcon from '@mui/icons-material/EastRounded';
import { ArrowBack } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useToast } from '../../hook/useToast';
import CommonButtonActionButton from '../common/CommonButtonActionButton';
import httpcommon from '../../helper/httpcommon';
import AuthContext from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { submitParentFeedbackForStudent } from '../../services/api';

const NAVY = '#292D5B';
const ORANGE = '#F6A03A';

const MotionBox = motion(Box);

const qKey = (q) => q.qid || q.id;

const toTitleCase = (str) =>
    String(str)
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

const isAnswered = (q, value) =>
    q.type === 'checkbox'
        ? Array.isArray(value) && value.length > 0
        : typeof value === 'string' && value.trim() !== '';

const OptionRow = ({ selected, label, onClick, multi }) => (
    <Box
        onClick={onClick}
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.75,
            borderRadius: '14px',
            cursor: 'pointer',
            border: '1.5px solid',
            borderColor: selected ? ORANGE : '#E7EAF1',
            backgroundColor: selected ? '#FFF9F0' : '#fff',
            transition: 'all .15s ease',
            '&:hover': {
                borderColor: selected ? ORANGE : '#CFD4E2',
                backgroundColor: selected ? '#FFF9F0' : '#FBFBFE',
                transform: 'translateY(-1px)',
            },
        }}
    >
        <Box
            sx={{
                width: 24,
                height: 24,
                flexShrink: 0,
                borderRadius: multi ? '8px' : '50%',
                border: '2px solid',
                borderColor: selected ? ORANGE : '#C4C9D6',
                backgroundColor: selected ? ORANGE : '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all .15s ease',
            }}
        >
            {selected && <CheckRoundedIcon sx={{ fontSize: 16, color: '#fff' }} />}
        </Box>
        <Typography sx={{ fontSize: 14.5, color: '#2C3350', fontWeight: selected ? 600 : 500, lineHeight: 1.45 }}>
            {label}
        </Typography>
    </Box>
);

const ParentFeedback = ({
    onComplete,
    targetUserId,
    targetStudentName,
    introText,
    submitButtonLabel = 'Submit',
    onBack,
    prefillSavedAnswers = false,
}) => {
    const [answers, setAnswers] = useState({});
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [questions, setQuestions] = useState([]);
    const [fetchingQuestions, setFetchingQuestions] = useState(false);
    const { user } = useContext(AuthContext);
    const [questionErrors, setQuestionErrors] = useState({});
    const [currentIndex, setCurrentIndex] = useState(0);
    const navigate = useNavigate();
    const advanceRef = useRef();
    const activeUserId = targetUserId || user?.userInfo?.user;

    const handleChange = (id, value) => {
        let updatedValue = value;

        if (Array.isArray(value) && value.includes('None')) {
            updatedValue = ['None'];
        }

        setAnswers((prev) => ({
            ...prev,
            [id]: updatedValue,
        }));
        setQuestionErrors((prev) => {
            if (!prev[id]) return prev;
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };

    const handleSubmit = async () => {
        if (loading) return;

        const errors = {};
        let hasError = false;

        questions.forEach((q) => {
            const questionId = qKey(q);
            const val = answers[questionId];

            if (q.type === 'checkbox') {
                if (!Array.isArray(val) || val.length === 0) {
                    errors[questionId] = true;
                    hasError = true;
                } else if (q.max && val.length > q.max) {
                    errors[questionId] = true;
                    hasError = true;
                    showToast(`You can select up to ${q.max} options for: "${q.label}"`, 'error');
                }
            } else {
                if (!val) {
                    errors[questionId] = true;
                    hasError = true;
                }
            }
        });

        setQuestionErrors(errors);

        if (hasError) {
            const firstBad = questions.findIndex((q) => errors[qKey(q)]);
            if (firstBad !== -1) setCurrentIndex(firstBad);
            showToast('Please answer all required questions.', 'error');
            return;
        }

        const responses = questions.map((q) => {
            const questionId = qKey(q);
            return {
                qid: questionId,
                answer: answers[questionId],
            };
        });

        try {
            setLoading(true);
            const response = await submitParentFeedbackForStudent({
                studentUserId: activeUserId,
                responses,
            });
            if (response.status === 200 || response.status === 201) {
                onComplete?.();
            } else {
                showToast('Failed to submit feedback.', 'error');
            }
        } catch (error) {
            console.error('Error submitting feedback:', error);
            showToast(error.response?.data?.error || 'Error submitting feedback', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchQuestions();
    }, [activeUserId]);

    useEffect(() => () => window.clearTimeout(advanceRef.current), []);
    useEffect(() => setCurrentIndex(0), [questions.length]);

    const fetchQuestions = async () => {
        if (!activeUserId) return;

        try {
            setFetchingQuestions(true);

            const questionRes = await httpcommon.get('school/feedback_questions/parent/');
            if (questionRes.status !== 200) {
                showToast('Failed to load questions.', 'error');
                return;
            }

            const fetchedQuestions = questionRes.data;
            setQuestions(fetchedQuestions);

            const defaultAnswers = {};
            fetchedQuestions.forEach((q) => {
                defaultAnswers[qKey(q)] = q.type === 'checkbox' ? [] : '';
            });

            if (prefillSavedAnswers) {
                try {
                    const answerRes = await httpcommon.get(`school/feedback/submission/${activeUserId}/parent/`);

                    if (answerRes.status === 200 && Array.isArray(answerRes.data?.responses)) {
                        answerRes.data.responses.forEach((resp) => {
                            defaultAnswers[resp.qid] = resp.answer;
                        });
                    }
                } catch (err) {
                    if (err.response?.status !== 404) {
                        showToast(err.response?.data?.error || 'Error fetching saved answers', 'error');
                    }
                }
            }

            setAnswers(defaultAnswers);
        } catch (error) {
            showToast(error.response?.data?.error || 'Error fetching data', 'error');
        } finally {
            setFetchingQuestions(false);
        }
    };

    /* -------------------- Wizard nav -------------------- */

    const total = questions.length;
    const question = questions[currentIndex];
    const isLast = currentIndex === total - 1;
    const answeredCount = questions.filter((q) => isAnswered(q, answers[qKey(q)])).length;
    const pct = total > 0 ? ((currentIndex + 1) / total) * 100 : 0;

    const R = 46;
    const CIRC = 2 * Math.PI * R;
    const ringOffset = total > 0 ? CIRC * (1 - answeredCount / total) : CIRC;

    const clearAdvance = () => window.clearTimeout(advanceRef.current);

    const goBack = () => {
        clearAdvance();
        setCurrentIndex((i) => Math.max(i - 1, 0));
    };

    const jumpTo = (i) => {
        clearAdvance();
        setCurrentIndex(i);
    };

    const goNext = () => {
        clearAdvance();
        if (!question) return;
        if (!isAnswered(question, answers[qKey(question)])) {
            setQuestionErrors((prev) => ({ ...prev, [qKey(question)]: true }));
            showToast('Please answer this question to continue.', 'error');
            return;
        }
        if (isLast) {
            handleSubmit();
            return;
        }
        setCurrentIndex((i) => Math.min(i + 1, total - 1));
    };

    const toggleCheckbox = (id, option) => {
        const cur = Array.isArray(answers[id]) ? answers[id] : [];
        let next;
        if (option === 'None') {
            next = cur.includes('None') ? [] : ['None'];
        } else {
            next = cur.includes(option)
                ? cur.filter((v) => v !== option)
                : [...cur.filter((v) => v !== 'None'), option];
        }
        handleChange(id, next);
    };

    const selectRadio = (id, option) => {
        handleChange(id, option);
        if (currentIndex < total - 1) {
            clearAdvance();
            advanceRef.current = window.setTimeout(
                () => setCurrentIndex((i) => Math.min(i + 1, total - 1)),
                280
            );
        }
    };

    const railTitle = targetStudentName
        ? `Feedback for ${targetStudentName}`
        : 'Parent / Teacher feedback';
    const railSubtitle =
        introText ||
        `A few quick questions about ${targetStudentName ? `${targetStudentName}'s` : 'the studentâ€™s'} strengths and interests â€” one at a time.`;

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: 940,
                mx: 'auto',
                borderRadius: '22px',
                overflow: 'hidden',
                border: '1px solid #E7EAF1',
                boxShadow: '0 18px 48px rgba(41, 45, 91, 0.08)',
                backgroundColor: '#fff',
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '340px 1fr' },
                minHeight: { xs: 'auto', md: 600 },
            }}
        >
            {/* â”€â”€ Left rail â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
            <Box
                sx={{
                    position: 'relative',
                    background: 'linear-gradient(160deg, #262B61 0%, #363C7C 55%, #4B3E86 100%)',
                    color: '#fff',
                    p: { xs: 2.5, md: 3.5 },
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: -70,
                        right: -70,
                        width: 200,
                        height: 200,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(246,160,58,0.35) 0%, rgba(246,160,58,0) 70%)',
                    }}
                />

                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                            onClick={() => (onBack ? onBack() : navigate(-1))}
                            sx={{
                                p: 0,
                                width: 30,
                                height: 30,
                                flexShrink: 0,
                                backgroundColor: 'rgba(255,255,255,0.12)',
                                borderRadius: '50%',
                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                            }}
                        >
                            <ArrowBack sx={{ fontSize: 17, color: '#fff' }} />
                        </IconButton>
                        <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '2px', opacity: 0.7 }}>
                            FEEDBACK
                        </Typography>
                    </Box>

                    <Typography sx={{ fontSize: { xs: 19, md: 22 }, fontWeight: 800, mt: 2, lineHeight: 1.2 }}>
                        {railTitle}
                    </Typography>
                    <Typography sx={{ fontSize: 13, opacity: 0.78, mt: 1, lineHeight: 1.6, maxWidth: 260 }}>
                        {railSubtitle}
                    </Typography>
                </Box>

                {/* Progress ring */}
                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        mt: { xs: 3, md: 'auto' },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                    }}
                >
                    <Box sx={{ position: 'relative', width: 108, height: 108, flexShrink: 0 }}>
                        <Box component="svg" viewBox="0 0 108 108" sx={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
                            <circle cx="54" cy="54" r={R} fill="none" stroke="rgba(255,255,255,0.16)" strokeWidth="7" />
                            <MotionBox
                                component="circle"
                                cx="54"
                                cy="54"
                                r={R}
                                fill="none"
                                stroke={ORANGE}
                                strokeWidth="7"
                                strokeLinecap="round"
                                strokeDasharray={CIRC}
                                animate={{ strokeDashoffset: ringOffset }}
                                transition={{ duration: 0.5, ease: 'easeOut' }}
                            />
                        </Box>
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1 }}>{answeredCount}</Typography>
                            <Typography sx={{ fontSize: 11, opacity: 0.7 }}>of {total || 'â€”'}</Typography>
                        </Box>
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, opacity: 0.9 }}>
                            Question {total ? currentIndex + 1 : 0} / {total || 'â€”'}
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, opacity: 0.6, mt: 0.25 }}>
                            {answeredCount === total && total > 0 ? 'All done â€” ready to submit' : 'answered so far'}
                        </Typography>
                    </Box>
                </Box>

                {/* Question dots */}
                {total > 0 && (
                    <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexWrap: 'wrap', gap: 0.9, mt: 2.5 }}>
                        {questions.map((q, i) => {
                            const done = isAnswered(q, answers[qKey(q)]);
                            const active = i === currentIndex;
                            return (
                                <Box
                                    key={qKey(q)}
                                    onClick={() => jumpTo(i)}
                                    sx={{
                                        width: 22,
                                        height: 22,
                                        borderRadius: '7px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 10.5,
                                        fontWeight: 800,
                                        color: done || active ? '#fff' : 'rgba(255,255,255,0.65)',
                                        backgroundColor: done ? ORANGE : 'rgba(255,255,255,0.12)',
                                        border: active ? '2px solid #fff' : '2px solid transparent',
                                        transition: 'all .15s ease',
                                        '&:hover': { backgroundColor: done ? ORANGE : 'rgba(255,255,255,0.22)' },
                                    }}
                                >
                                    {i + 1}
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Box>

            {/* â”€â”€ Right panel â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, backgroundColor: '#FCFCFE' }}>
                {fetchingQuestions ? (
                    <Box display="flex" justifyContent="center" alignItems="center" sx={{ flex: 1, py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : total === 0 ? (
                    <Box display="flex" justifyContent="center" alignItems="center" sx={{ flex: 1, py: 8 }}>
                        <Typography variant="body2">No questions available.</Typography>
                    </Box>
                ) : question ? (
                    <>
                        {/* Slim progress line */}
                        <Box sx={{ height: 4, backgroundColor: '#EEF0F5', flexShrink: 0 }}>
                            <MotionBox
                                animate={{ width: `${pct}%` }}
                                transition={{ duration: 0.35, ease: 'easeOut' }}
                                sx={{ height: '100%', background: 'linear-gradient(90deg, #F6A03A, #F7B65C)' }}
                            />
                        </Box>

                        <MotionBox
                            key={qKey(question)}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.28, ease: 'easeOut' }}
                            sx={{
                                flex: { xs: 'none', md: 1 },
                                minHeight: 0,
                                overflowY: { xs: 'visible', md: 'auto' },
                                px: { xs: 2.5, md: 4.5 },
                                pt: { xs: 3, md: 4 },
                                pb: 3,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Box
                                    sx={{
                                        px: 1,
                                        height: 24,
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        fontSize: 12,
                                        fontWeight: 800,
                                        color: ORANGE,
                                        backgroundColor: '#FFF4E4',
                                    }}
                                >
                                    Q{currentIndex + 1}
                                </Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#9AA0B2', letterSpacing: '0.4px' }}>
                                    {question.type === 'checkbox'
                                        ? question.max
                                            ? `SELECT UP TO ${question.max}`
                                            : 'SELECT ALL THAT APPLY'
                                        : 'CHOOSE ONE'}
                                </Typography>
                            </Box>

                            <Typography
                                sx={{
                                    fontSize: { xs: 18, md: 21 },
                                    fontWeight: 800,
                                    color: NAVY,
                                    lineHeight: 1.35,
                                    mb: 3,
                                    maxWidth: 620,
                                }}
                            >
                                {question.label}
                            </Typography>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        lg: (question.options?.length || 0) > 5 ? '1fr 1fr' : '1fr',
                                    },
                                    gap: 1.25,
                                    maxWidth: 760,
                                }}
                            >
                                {question.type === 'checkbox'
                                    ? (question.options || []).map((option) => {
                                          const val = Array.isArray(answers[qKey(question)]) ? answers[qKey(question)] : [];
                                          return (
                                              <OptionRow
                                                  key={option}
                                                  multi
                                                  selected={val.includes(option)}
                                                  label={toTitleCase(option)}
                                                  onClick={() => toggleCheckbox(qKey(question), option)}
                                              />
                                          );
                                      })
                                    : (question.options || []).map((option) => (
                                          <OptionRow
                                              key={option}
                                              selected={answers[qKey(question)] === option}
                                              label={toTitleCase(option)}
                                              onClick={() => selectRadio(qKey(question), option)}
                                          />
                                      ))}
                            </Box>

                            {questionErrors[qKey(question)] && (
                                <Typography sx={{ color: '#E5484D', fontSize: 13, fontWeight: 600, mt: 2 }}>
                                    Please answer this question to continue.
                                </Typography>
                            )}
                        </MotionBox>

                        {/* Footer */}
                        <Box
                            sx={{
                                flexShrink: 0,
                                px: { xs: 2.5, md: 4.5 },
                                py: 2,
                                borderTop: '1px solid #EEF0F5',
                                backgroundColor: '#fff',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 1.5,
                            }}
                        >
                            <Button
                                onClick={goBack}
                                disabled={currentIndex === 0}
                                startIcon={<WestRoundedIcon />}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    fontSize: 14,
                                    color: '#7A8096',
                                    borderRadius: '10px',
                                    px: 1.5,
                                    '&.Mui-disabled': { opacity: 0.35 },
                                }}
                            >
                                Back
                            </Button>

                            <CommonButtonActionButton
                                onClick={goNext}
                                disabled={loading}
                                endIcon={!isLast ? <EastRoundedIcon /> : undefined}
                                sx={{
                                    minWidth: 180,
                                    borderRadius: '12px',
                                    py: 1.1,
                                    fontWeight: 700,
                                    fontSize: 15,
                                    textTransform: 'none',
                                    boxShadow: loading ? 'none' : '0 10px 22px rgba(246, 160, 58, 0.32)',
                                }}
                            >
                                {isLast ? (loading ? 'Submittingâ€¦' : submitButtonLabel) : 'Next'}
                            </CommonButtonActionButton>
                        </Box>
                    </>
                ) : null}
            </Box>
        </Box>
    );
};

export default ParentFeedback;

